import { TrackJS } from 'trackjs';

import shuffle from 'lodash/shuffle';

import { ActionHelper, AppConfig } from '../../core-bundle';

import { WallpaperConfig } from '../config/wallpaper-config';
import { RotationService } from '../services/rotation-service';
import { determineWallpaperProvider } from '../services/providers';

import { HttpResponseCodes } from '@module/http/axios/response';

export const ActionTypes = ActionHelper.generateTypes([
  'PAUSE_WALLPAPER_ROTATION',
  'RESUME_WALLPAPER_ROTATION',
  'ROTATE_WALLPAPER',
  'STOP_WALLPAPER_ROTATION',
  'WALLPAPER_ERROR',
  'WALLPAPER_BLOCKED_BY_CLIENT',
]);

const config = {
  ...AppConfig,
  channelWallpapers: shuffle(AppConfig.channelWallpapers),
  ...WallpaperConfig,
};

function scheduleRotation(rotationInterval, callback) {
  RotationService.stop();
  RotationService.start(rotationInterval, callback);
}

function rotateWallpaper() {
  return async (dispatch, getState) => {
    const state = getState();

    try {
      // We decided to determine the wallpaper provider on every rotation,
      // to avoid making provider decisions somewhere else. For example:
      // When users signs up, they haven't uploaded a wallpaper yet. In that case,
      // we show editorial wallpapers. When the user uploads wallpaper(s), we must
      // switch the provider. One way is dispatching an action from the upload
      // form, but that implies that the form must know about wallpapers, which
      // breaks the Single Responsibility Principle. This is a cheap operation,
      // we should be fine™
      const provider = determineWallpaperProvider(config, state);

      scheduleRotation(
        // Rotation interval can be different based on the rotation position.
        provider.rotationInterval(config, state),
        () => dispatch(rotateWallpaper())
      );

      const isRotationEnabled = provider.isRotationEnabled(config, state);
      const providerHasChanged = state.wallpaper.providerName !== provider.name;
      if (isRotationEnabled || providerHasChanged) {
        // We need access to the latest state to determine the next creative.
        // Since this method is self-invoked based in the rotation interval,
        // the state cannot be passed from a component.
        const nextCreative = await provider.nextCreative(config, state);

        return dispatch({
          type: ActionTypes.ROTATE_WALLPAPER,
          creative: nextCreative,
          isRotationEnabled,
          providerName: provider.name,
        });
      }
    } catch (error) {
      // The network request has been blocked by the client.
      // Most probably because of adblockers or content restrictions.
      if (error.status === HttpResponseCodes.BLOCKED_BY_CLIENT) {
        dispatch({ type: ActionTypes.WALLPAPER_BLOCKED_BY_CLIENT });
        dispatch(rotateWallpaper());
        return;
      }

      TrackJS.track(error);

      dispatch({ type: ActionTypes.WALLPAPER_ERROR });
      dispatch(rotateWallpaper());
    }
  };
}

function pauseRotation(isHidden = false) {
  return (dispatch) => {
    RotationService.pause();

    return dispatch({
      type: ActionTypes.PAUSE_WALLPAPER_ROTATION,
      isHidden,
    });
  };
}

function startOrResumeRotation(wallpaper) {
  return (dispatch) => {
    RotationService.startOrResume(wallpaper.rotationInterval, () =>
      dispatch(rotateWallpaper())
    );

    return dispatch({
      type: ActionTypes.RESUME_WALLPAPER_ROTATION,
    });
  };
}

function showFallbackWallpaper() {
  return (dispatch) => {
    dispatch({ type: ActionTypes.WALLPAPER_ERROR });
    dispatch(rotateWallpaper());
  };
}

export const WallpaperActions = ActionHelper.generateActions({
  pauseRotation,
  rotateWallpaper,
  startOrResumeRotation,
  showFallbackWallpaper,
});
